import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BookOpen,
  Check,
  ChevronRight,
  CreditCard,
  ExternalLink,
  MessageCircle,
  Play,
  Sparkles,
  Users,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ASSETS, COACH_WHATSAPP, WHATSAPP_COMMUNITY } from "@/constants/assets";
import Footer from "@/components/Footer";

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [modules, setModules] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: m }, { data: p }] = await Promise.all([
        api.get("/dashboard"),
        api.get("/modules"),
        api.get("/plans"),
      ]);
      setStats(d);
      setModules(m);
      setPlans(p);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadAll();
  }, [user, loadAll]);

  const toggleModule = async (mod) => {
    try {
      await api.post("/modules/complete", { module_id: mod.id, completed: !mod.completed });
      await Promise.all([loadAll(), refresh()]);
      toast.success(!mod.completed ? "Module marqué comme terminé ✨" : "Module réinitialisé");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!user) return null;

  const whatsappCoachUrl = `https://wa.me/${COACH_WHATSAPP}?text=${encodeURIComponent(
    `Bonjour Roméo, je suis ${user.full_name}, membre de PROSPECT'HEURE.`,
  )}`;

  return (
    <div className="min-h-screen bg-ph" data-testid="dashboard-page">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3"
            data-testid="dashboard-logo-link"
          >
            <img src={ASSETS.logo} alt="" className="h-10 w-10 object-contain" />
            <div className="leading-tight text-left">
              <div className="font-serif-lux text-lg text-[#9A762F]">PROSPECT&rsquo;HEURE</div>
              <div className="text-[10px] tracking-[0.25em] text-[#667085] uppercase">
                Espace membre
              </div>
            </div>
          </button>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-sm text-[#172033]">
              {user.full_name} · <span className="text-[#9A762F]">{user.city}</span>
            </div>
            <button
              className="text-xs text-[#667085] hover:text-[#C9A45C] tracking-[0.2em] uppercase"
              onClick={() => {
                logout();
                navigate("/");
              }}
              data-testid="dashboard-logout"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-xs text-[#9A762F] tracking-[0.22em] uppercase font-semibold">Espace membre · Institut PROSPECT&rsquo;HEURE</div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B1726] leading-tight mt-2 tracking-tight">
            Bonjour, <span className="text-[#9A762F]">{user.full_name.split(" ")[0]}</span>.
          </h1>
          <p className="text-[#475467] mt-2 max-w-xl">
            Voici votre tableau de bord. Chaque module terminé rapproche votre activité de son
            plein potentiel.
          </p>
        </motion.section>

        {/* Payment status banner */}
        {!user.is_paid && (
          <motion.section
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="ph-card-dark rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            data-testid="payment-banner"
          >
            <div>
              <div className="text-[11px] text-[#E8D2A1] tracking-[0.22em] uppercase font-semibold">Votre compte est créé</div>
              <div className="text-xl md:text-2xl text-white font-semibold mt-2">
                Votre inscription n&rsquo;est pas encore finalisée.
              </div>
              <p className="text-[14px] text-[#D0D5DD] mt-2 max-w-xl">
                Procédez au paiement de votre formation pour débloquer l&rsquo;accès complet à l&rsquo;entraînement officiel.
              </p>
            </div>
            <a
              href="#dashboard-plans"
              onClick={(e) => { e.preventDefault(); document.getElementById("dashboard-plans")?.scrollIntoView({ behavior: "smooth" }); }}
              className="gold-btn-accent gold-btn-pill inline-flex items-center gap-2 px-6 py-3 shrink-0"
              data-testid="payment-banner-cta"
            >
              Procéder au paiement <ChevronRight size={16} />
            </a>
          </motion.section>
        )}

        {/* Stats + Progress */}
        <section className="grid lg:grid-cols-3 gap-6" data-testid="dashboard-stats">
          <div className="lg:col-span-2 ph-card rounded-sm p-8">
            <div className="flex items-center justify-between">
              <div className="text-xs text-[#9A762F] tracking-[0.3em] uppercase">Progression</div>
              <div className="font-serif-lux text-3xl text-[#9A762F]">{stats?.progress ?? 0}%</div>
            </div>
            <div className="mt-4 h-2 bg-[#FFFFFF] border border-[#E4E7EC] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stats?.progress ?? 0}%` }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="h-full bg-gradient-to-r from-[#A2823F] to-[#E8D2A1]"
              />
            </div>
            <div className="mt-6 grid grid-cols-3 gap-4">
              <Stat label="Modules totaux" value={stats?.modules_total ?? 0} />
              <Stat label="Terminés" value={stats?.modules_completed ?? 0} />
              <Stat label="Restants" value={stats?.modules_remaining ?? 0} />
            </div>
          </div>

          <div className="ph-card rounded-sm p-8 flex flex-col">
            <div className="text-xs text-[#9A762F] tracking-[0.3em] uppercase">Prochaine étape</div>
            {stats?.next_module ? (
              <>
                <div className="font-serif-lux text-2xl text-[#0B1726] mt-3">{stats.next_module.title}</div>
                <div className="text-sm text-[#667085] mt-1">Durée : {stats.next_module.duration}</div>
                <button
                  onClick={() =>
                    document.getElementById(`module-${stats.next_module.id}`)?.scrollIntoView({
                      behavior: "smooth",
                    })
                  }
                  className="gold-btn mt-auto py-2.5 rounded-sm text-sm inline-flex items-center justify-center gap-2"
                  data-testid="dashboard-next-module"
                >
                  Continuer <ChevronRight size={16} />
                </button>
              </>
            ) : (
              <>
                <div className="font-serif-lux text-2xl text-[#0B1726] mt-3">Tous les modules sont terminés 🎉</div>
                <div className="text-sm text-[#667085] mt-1">Contactez l&rsquo;entraîneur pour la suite.</div>
              </>
            )}
          </div>
        </section>

        {/* Action row */}
        <section className="grid md:grid-cols-2 gap-6">
          <ActionCard
            testid="action-coach-whatsapp"
            icon={MessageCircle}
            title="Discuter avec l'Entraîneur d'Excellence Roméo Chouaké sur WhatsApp"
            subtitle="Réponse rapide, échanges privés"
            href={whatsappCoachUrl}
            cta="Ouvrir WhatsApp"
            highlighted
          />
          <ActionCard
            testid="action-community"
            icon={Users}
            title="Communauté officielle"
            subtitle={`Votre code d'accès : ${user.community_code || "PH-XXXXXXXX"}`}
            href={WHATSAPP_COMMUNITY}
            cta="Rejoindre la communauté"
          />
        </section>

        {/* Pricing plans — only visible here */}
        <section id="dashboard-plans" data-testid="dashboard-plans">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-[#9A762F] text-xs tracking-[0.3em] uppercase">
                <Sparkles size={14} /> Débloquer l&rsquo;entraînement
              </div>
              <h2 className="font-serif-lux text-3xl md:text-4xl text-[#0B1726] mt-2">
                Choisissez votre formule.
              </h2>
              <p className="text-sm text-[#667085] mt-2 max-w-lg">
                Le paiement s&rsquo;effectue via <span className="text-[#9A762F]">Chariow</span>. Une
                fois payé, votre accès complet est activé automatiquement.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {plans.map((p) => (
              <PlanCard key={p.id} plan={p} />
            ))}
          </div>
        </section>

        {/* Modules */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs text-[#9A762F] tracking-[0.3em] uppercase">Modules</div>
              <h2 className="font-serif-lux text-3xl text-[#0B1726] mt-2">L&rsquo;entraînement</h2>
            </div>
            <div className="text-xs text-[#667085]">
              Cochez au fur et à mesure — votre progression est enregistrée automatiquement.
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-[#667085]" data-testid="dashboard-loading">Chargement…</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="modules-list">
              {modules.map((m, i) => (
                <motion.button
                  id={`module-${m.id}`}
                  key={m.id}
                  onClick={() => toggleModule(m)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  className={`text-left ph-card rounded-sm p-6 group hover:border-[#C9A45C] transition-colors ${
                    m.completed ? "border-[#C9A45C]" : ""
                  }`}
                  data-testid={`module-item-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div
                        className={`h-9 w-9 rounded-full border flex items-center justify-center shrink-0 ${
                          m.completed
                            ? "border-[#C9A45C] bg-[#C9A45C] text-[#F8F9FB]"
                            : "border-[#E4E7EC] text-[#9A762F]"
                        }`}
                      >
                        {m.completed ? <Check size={16} /> : <Play size={14} />}
                      </div>
                      <div>
                        <div className="text-xs text-[#9A762F] tracking-[0.25em] uppercase">Module {i + 1}</div>
                        <div className="font-serif-lux text-xl text-[#0B1726] mt-1 leading-tight">{m.title}</div>
                        <div className="text-xs text-[#667085] mt-1">Durée : {m.duration}</div>
                      </div>
                    </div>
                    <BookOpen size={18} className="text-[#667085] group-hover:text-[#9A762F] transition-colors" />
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-serif-lux text-3xl text-[#0B1726]">{value}</div>
      <div className="text-[10px] tracking-[0.25em] text-[#667085] uppercase mt-1">{label}</div>
    </div>
  );
}

function ActionCard({ icon: Icon, title, subtitle, href, cta, highlighted, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`ph-card rounded-sm p-6 flex flex-col justify-between hover:border-[#C9A45C] transition-colors group ${
        highlighted ? "border-[#C9A45C]" : ""
      }`}
      data-testid={testid}
    >
      <div>
        <div
          className={`h-10 w-10 rounded-sm flex items-center justify-center ${
            highlighted ? "bg-[#C9A45C] text-[#F8F9FB]" : "bg-[#FFFFFF] text-[#9A762F] border border-[#E4E7EC]"
          }`}
        >
          <Icon size={18} />
        </div>
        <div className="font-serif-lux text-xl text-[#0B1726] mt-4">{title}</div>
        <div className="text-sm text-[#667085] mt-1">{subtitle}</div>
      </div>
      <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#9A762F] group-hover:text-[#A2823F]">
        {cta} <ExternalLink size={14} />
      </div>
    </a>
  );
}

function PlanCard({ plan }) {
  const elite = plan.highlighted;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative ph-card rounded-sm p-8 ${elite ? "border-[#C9A45C]" : ""}`}
      data-testid={`plan-${plan.id}`}
    >
      {elite && (
        <div className="absolute -top-3 left-8 bg-[#C9A45C] text-[#F8F9FB] text-[10px] tracking-[0.25em] uppercase px-3 py-1">
          Recommandé
        </div>
      )}
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs text-[#9A762F] tracking-[0.3em] uppercase">
            {elite ? "Formule Élite" : "Formule Officielle"}
          </div>
          <div className="font-serif-lux text-3xl text-[#0B1726] mt-1">{plan.name}</div>
          <div className="text-sm text-[#667085] mt-1">{plan.tagline}</div>
        </div>
        <div className="text-right">
          <div className="font-serif-lux text-5xl text-[#9A762F] leading-none">${plan.price}</div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#667085] mt-1">{plan.currency}</div>
        </div>
      </div>

      <div className="my-6 gold-underline" />

      <ul className="space-y-3">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[#172033]">
            <span className="mt-0.5 text-[#9A762F]">
              <Check size={16} />
            </span>
            {f}
          </li>
        ))}
      </ul>

      <a
        href={plan.checkout_url}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-8 inline-flex items-center justify-center gap-2 w-full py-3 rounded-sm text-sm ${
          elite ? "gold-btn" : "gold-btn-outline"
        }`}
        data-testid={`plan-${plan.id}-pay`}
      >
        <CreditCard size={16} /> Payer via Chariow
      </a>
    </motion.div>
  );
}
